import * as React from 'react'
import { FC } from 'react'
import { BaseTextField } from '@user/components/Formik'
import { Field } from 'formik'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import { LocationMenu, NoteCode } from '@user/components/Notes/typings'
import MenuItem from '@mui/material/MenuItem'
import { InfoTooltip } from '@sales-center/components'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import FormHelperText from '@mui/material/FormHelperText'
import NoteCodeSelect from '@user/components/Notes/AddNoteDrawer/AddNoteForm/NoteCodeSelect'
import { Theme } from '@mui/material/styles'
import Autocomplete from '@mui/material/Autocomplete'

interface Props {
  externalLocationId?: string
  serviceOptions: LocationMenu[]
  noteCodes: NoteCode[]
  noteFieldName: string
  noteFieldHelp: string | boolean
  noteFieldError: string | boolean | undefined
  selectWidth?: string
  toggleName: string
  toggleValue: boolean | undefined
  requiredAddNote?: boolean
  requiredOtherFields?: boolean
  serviceLocationFieldName?: string
  serviceLocationHelp?: string | boolean
  serviceLocationError?: string | boolean | undefined
  noteCodeFieldName?: string
  noteCodeHelp: string | boolean
  noteCodeError: string | boolean | undefined
  toggleVMargin?: number
  maxNoteLines?: number
  noteCode?: string | null
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  serviceLocation?: string | undefined
  fromMapView?: boolean
}

export const NoteFormFieldElements: FC<Props> = (props) => {
  const {
    fromMapView = false,
    serviceOptions,
    externalLocationId,
    noteCodes,
    noteFieldName,
    noteFieldError,
    noteFieldHelp,
    toggleName,
    toggleValue,
    requiredAddNote = true,
    requiredOtherFields = true,
    selectWidth,
    serviceLocationFieldName = 'serviceLocation',
    serviceLocationHelp,
    serviceLocationError,
    noteCodeFieldName = 'code',
    noteCodeHelp,
    noteCodeError,
    toggleVMargin,
    maxNoteLines,
    noteCode,
    setFieldValue,
    serviceLocation,
  } = props

  const { externalProduct } = useSelector(selectCurrentUser)

  return (
    <>
      <Field
        name={noteFieldName}
        autoComplete="off"
        component={BaseTextField}
        error={noteFieldError}
        fullWidth
        helperText={noteFieldHelp}
        inputProps={{
          maxLength: 5000,
        }}
        label="Add Note"
        multiline
        required={requiredAddNote}
        minRows={fromMapView ? 2 : 5}
        maxRows={10}
        type="text"
        variant="outlined"
        avoidValidateOnBlur
        {...(maxNoteLines && { maxRows: maxNoteLines })}
        data-test-id="note-field"
      />
      {externalLocationId && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            mt={toggleVMargin || 2}
            mb={toggleValue ? toggleVMargin || 2.5 : 0}
            ml={0}
            gap={0.5}
          >
            <FormControlLabel
              sx={{ width: 'fit-content', m: 0 }}
              labelPlacement="end"
              data-test-id="add-note-to-location-toggle"
              label={
                <Typography
                  variant="body1"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  Add note to {externalProduct} Location
                </Typography>
              }
              control={
                <Field
                  type="checkbox"
                  name={toggleName}
                  as={Switch}
                  size="small"
                />
              }
            />

            <InfoTooltip
              sx={{ color: (theme: Theme) => theme.palette.action.active }}
              fontSize={24}
              infoText={`This note will be saved on the selected location in ${externalProduct} and will also be viewable in Sales Center`}
            />
          </Stack>

          {toggleValue && (
            <>
              {serviceOptions?.length > 0 && (
                <Field
                  data-test-id="service-location-select"
                  label="Service Location"
                  name={serviceLocationFieldName}
                  component={Autocomplete}
                  value={
                    serviceOptions.find(
                      (option) => option.value === serviceLocation,
                    ) || null
                  }
                  onChange={(_e, value: LocationMenu | null) => {
                    setFieldValue(
                      serviceLocationFieldName,
                      value ? value.value : null,
                    )
                  }}
                  options={serviceOptions}
                  getOptionLabel={(option: LocationMenu) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        marginBottom: (theme: Theme) => theme.spacing(2),
                        width: selectWidth || '350px',
                      }}
                      label="Service Location"
                      required={requiredOtherFields}
                      inputProps={{
                        ...params.inputProps,
                        name: serviceLocationFieldName,
                      }}
                      error={Boolean(serviceLocationError)}
                      helperText={serviceLocationHelp}
                    />
                  )}
                  renderOption={(props, location: LocationMenu) => (
                    <MenuItem
                      {...props}
                      key={`locations-${location.value}`}
                      value={location.value}
                    >
                      {location.label}
                    </MenuItem>
                  )}
                />
              )}

              <NoteCodeSelect
                value={noteCode || null}
                label={'Note Code'}
                name={noteCodeFieldName}
                required={requiredOtherFields}
                options={noteCodes}
                setFieldValue={setFieldValue}
                selectWidth={selectWidth}
                error={noteCodeError}
              />

              {noteCodeHelp && (
                <FormHelperText sx={{ ml: 1 }} error>
                  {noteCodeHelp}
                </FormHelperText>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

NoteFormFieldElements.displayName = 'NoteFormFieldElements'
